"use client";

import type { FC, PropsWithChildren } from "react";
import { createContext, useContext, useMemo } from "react";

import {
  BLOG_URL,
  FAQ_URL,
  LOYALTY_BLOG_URL,
  NESTED_STORE_LOCATOR_URL,
  SHOP_URL,
} from "@/app/_internal/sitemap/consts";
import { SHOP_GEOGRAPHICAL_AREA_PAGE_SLUG } from "@/app/[locale]/(default)/shop-geographical-area/[geographicalArea]/constants";
import type { RewriteConfig } from "@/env";
import { env } from "@/env";

export const RewriteUrlsContext = createContext<{
  rewriteConfig: RewriteConfig;
  isGeographicalAreaFilteringEnabled: boolean;
}>({ rewriteConfig: {}, isGeographicalAreaFilteringEnabled: false });

export const RewriteUrlsProvider: FC<PropsWithChildren<{ locale: string }>> = ({ children, locale }) => {
  const value = useMemo(
    () => ({
      rewriteConfig: env.rewriteUrlsConfig?.[locale] || {},
      isGeographicalAreaFilteringEnabled: env.getIsGeographicalAreaFilteringEnabled(locale),
    }),
    [locale]
  );

  return <RewriteUrlsContext.Provider value={value}>{children}</RewriteUrlsContext.Provider>;
};

export const useRewriteUrls = (): {
  shop: string;
  shopGeographicalArea: string;
  blog: string;
  loyaltyBlog: string;
  faq: string;
  nestedStoreLocator: string;
} => {
  const { rewriteConfig, isGeographicalAreaFilteringEnabled } = useContext(RewriteUrlsContext);

  return useMemo(
    () => ({
      shop: rewriteConfig?.shop || SHOP_URL,
      shopGeographicalArea: isGeographicalAreaFilteringEnabled
        ? rewriteConfig?.[SHOP_GEOGRAPHICAL_AREA_PAGE_SLUG] || SHOP_GEOGRAPHICAL_AREA_PAGE_SLUG
        : "",
      blog: rewriteConfig?.blog || BLOG_URL,
      loyaltyBlog: rewriteConfig?.["loyalty-blog"] || LOYALTY_BLOG_URL,
      faq: rewriteConfig?.faq || FAQ_URL,
      nestedStoreLocator: rewriteConfig?.["nested-store-locator"] || NESTED_STORE_LOCATOR_URL,
    }),
    [rewriteConfig, isGeographicalAreaFilteringEnabled]
  );
};
